import { formatDate as formatWithPattern } from '@vueuse/core'
import type { InstrumentDto } from '../models/generated/domain-models'

const SUPERSCRIPT_DIGITS: Record<string, string> = {
  '-': '⁻',
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
}

export const formatScientific = (value: number, suffix: string = ''): string => {
  const exponent = Math.floor(Math.log10(Math.abs(value)))
  const mantissa = value / Math.pow(10, exponent)
  const superscriptExp = String(exponent)
    .split('')
    .map(c => SUPERSCRIPT_DIGITS[c] || c)
    .join('')
  return `${mantissa.toFixed(2)} × 10${superscriptExp}${suffix}`
}

const ACRONYMS = [
  'ETF',
  'FT',
  'API',
  'USD',
  'EUR',
  'GBP',
  'JPY',
  'CHF',
  'CAD',
  'AUD',
  'CEO',
  'CFO',
  'IT',
  'AI',
]

export const formatAcronym = (value: string | undefined | null): string => {
  if (!value) return '-'

  if (ACRONYMS.includes(value.toUpperCase())) {
    return value.toUpperCase()
  }

  const withSpaces = value.replace(/_/g, ' ')
  return withSpaces
    .split(' ')
    .map(word => {
      if (ACRONYMS.includes(word.toUpperCase())) return word.toUpperCase()
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')
}

const EUR_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
})

const AMOUNT_FORMAT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const formatCurrencyWithSymbol = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return '€0.00'
  return EUR_FORMAT.format(value)
}

export const formatCurrency = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return '0.00'
  return AMOUNT_FORMAT.format(Math.abs(value))
}

export const formatCurrencyWithSign = (
  value: number | undefined | null,
  currency?: string
): string => {
  if (value === null || value === undefined) return '0.00'

  const absValue = AMOUNT_FORMAT.format(Math.abs(value))
  const currencySymbol = getCurrencySymbol(currency)

  return `${currencySymbol}${absValue}`
}

const signFor = (value: number): string => (value < 0 ? '−' : '')

export const formatSignedCurrency = (value: number, currency: string | undefined): string =>
  `${signFor(value)}${formatCurrencyWithSign(value, currency)}`

export const formatSignedNumber = (value: number, decimals: number): string =>
  `${signFor(value)}${Math.abs(value).toFixed(decimals)}`

export const formatSignedPercent = (value: number, decimals: number = 2): string =>
  `${formatSignedNumber(value, decimals)}%`

const plusFor = (value: number): string => (value > 0 ? '+' : '')

export const formatCurrencyChange = (value: number): string => {
  const rounded = Number(value.toFixed(2))
  return `${plusFor(rounded)}${formatSignedCurrency(rounded, 'EUR')}`
}

export const splitCents = (amount: string): [string, string] => {
  const point = amount.lastIndexOf('.')
  return point < 0 ? [amount, ''] : [amount.slice(0, point), amount.slice(point)]
}

export const formatPercentChange = (value: number, decimals: number = 2): string => {
  const rounded = Number(value.toFixed(decimals))
  return `${plusFor(rounded)}${formatSignedPercent(rounded, decimals)}`
}

const PRICE_THRESHOLD_NO_DECIMALS = 10000
const PRICE_THRESHOLD_ONE_DECIMAL = 1000

export const formatPrice = (value: number | undefined | null, currency?: string): string => {
  if (value === null || value === undefined) return '0.00'

  const absValue = Math.abs(value)
  const currencySymbol = getCurrencySymbol(currency)

  let fractionDigits: number
  if (absValue >= PRICE_THRESHOLD_NO_DECIMALS) {
    fractionDigits = 0
  } else if (absValue >= PRICE_THRESHOLD_ONE_DECIMAL) {
    fractionDigits = 1
  } else {
    fractionDigits = 2
  }

  const formatted = absValue.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })

  return `${currencySymbol}${formatted}`
}

export const getCurrencySymbol = (currency?: string): string => {
  switch (currency?.toUpperCase()) {
    case 'EUR':
      return '€'
    case 'USD':
      return '$'
    case 'GBP':
      return '£'
    default:
      return '€'
  }
}

export const formatPercentageFromDecimal = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return 'N/A'
  return `${(value * 100).toFixed(2)}%`
}

export const formatProfitLoss = (
  value: number | null | undefined,
  includeSign: boolean = true
): string => {
  if (value === null || value === undefined) return '0.00'
  const formattedValue = AMOUNT_FORMAT.format(Math.abs(value))
  if (!includeSign) return formattedValue
  const sign = value >= 0 ? '+' : '-'
  return `${sign}${formattedValue}`
}

export const formatTransactionAmount = (
  quantity: number,
  price: number,
  type: string,
  currency?: string,
  includeSign: boolean = true
): string => {
  const amount = quantity * price

  const formattedAmount = AMOUNT_FORMAT.format(amount)

  const currencySymbol = getCurrencySymbol(currency)

  if (!includeSign) {
    return `${currencySymbol}${formattedAmount}`
  }

  const sign = type === 'BUY' ? '+' : '-'
  return `${sign}${currencySymbol}${formattedAmount}`
}

export const getProfitClass = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  return value >= 0 ? 'text-gain' : 'text-loss'
}

export const getGainLossClass = (value: number | null | undefined): string => {
  if (value === 0) return ''
  return getProfitClass(value)
}

export const getAmountClass = (type: string): string => {
  return type === 'BUY' ? 'text-gain' : 'text-loss'
}

export const formatDate = (dateString: string): string => {
  if (!dateString) return ''
  const date = new Date(dateString)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear()).slice(-2)

  return `${day}.${month}.${year}`
}

export const formatDateTime = (dateString: string): string =>
  dateString ? formatWithPattern(new Date(dateString), 'DD.MM HH:mm') : ''

export const ago = (value: string | null): string => {
  if (!value) return 'Never'
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`
  return `${Math.round(minutes / 1440)} d ago`
}

export const formatQuantity = (value: number | string | undefined | null): string => {
  if (value === null || value === undefined) return '0.0000'

  const numValue = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(numValue) || !isFinite(numValue)) return '0.0000'
  if (numValue === 0) return '0.0000'

  const absValue = Math.abs(numValue)
  if (absValue < 0.0001) return formatScientific(numValue)
  if (absValue >= 100) return numValue.toFixed(2)
  if (absValue >= 10) return numValue.toFixed(3)
  return numValue.toFixed(4)
}

export const formatPriceChange = (item: InstrumentDto): string => {
  const amount = item.priceChangeAmount
  const percent = item.priceChangePercent

  if (amount === null || amount === undefined || percent === null || percent === undefined) {
    return '-'
  }

  return `${formatSignedCurrency(amount, item.baseCurrency)} / ${formatSignedPercent(percent)}`
}

export const formatPercentage = (value: number): string => `${value.toFixed(2)}%`

export const formatHoldingPercentage = (value: number): string => {
  if (value === 0) return '<0.0001%'
  return value < 0.01 ? `${value.toFixed(4)}%` : formatPercentage(value)
}

export const formatTer = (value: number | null, decimals: number = 2): string => {
  if (value === null) return '-'
  return `${value.toFixed(decimals)}%`
}

export const formatReturn = (value: number | null): string => {
  if (value === null) return '-'
  return `${(value * 100).toFixed(2)}%`
}

export const formatRelativeTime = (timestampMs: number, nowMs: number): string => {
  const diffMs = nowMs - timestampMs
  const diffMinutes = Math.floor(diffMs / 60000)
  if (diffMinutes < 1) return 'just now'
  if (diffMinutes === 1) return '1 min ago'
  if (diffMinutes < 60) return `${diffMinutes} min ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours === 1) return '1 hour ago'
  return `${diffHours} hours ago`
}
